/**
 * Hard-refresh the app past every cache layer.
 *
 * window.location.reload() is a "soft" reload — browsers may serve a stale
 * index.html out of disk cache (especially entries cached before nginx grew
 * its no-cache headers, which bit Henry on 2026-04-25 and recurs whenever a
 * user's browser has a heuristic-cached pre-fix entry).
 *
 * Force a hard refresh by:
 *  1. Wiping any Cache API entries (PWA / Workbox caches, even though we
 *     don't ship a service worker today — defensive for future).
 *  2. Unregistering any service workers.
 *  3. Navigating with a cache-busting query param so the browser treats
 *     index.html as a different URL and re-fetches from origin.
 *
 * Shared by UpdateAvailableBanner (user-initiated) and ForceReloadDialog
 * (countdown-initiated) so both paths bust caches identically.
 */
export async function hardRefresh(cacheBustKey?: string | null): Promise<void> {
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
  } catch {
    // Cache cleanup is best-effort; proceed to navigate regardless.
  }
  const url = new URL(window.location.href)
  url.searchParams.set('_v', cacheBustKey || String(Date.now()))
  window.location.replace(url.toString())
}
